import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { galeriaApi } from '../../lib/api';
import logger from '../../lib/logger';
import { Images, ArrowLeft, Eye, EyeOff, Star, Trash2, ChevronLeft, ChevronRight, ExternalLink, Plus, Archive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { PhotoUploader } from '../../components/admin/PhotoUploader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AlbumRow = ({ album, onToggle, onMove, first, last }) => (
  <div className="flex items-center gap-4 p-4 rounded-xl bg-obsidian-200 border border-white/10" data-testid={`admin-album-${album.album_id}`}>
    <div className="w-20 h-16 rounded-lg bg-obsidian-50 overflow-hidden flex-shrink-0 flex items-center justify-center text-gray-600">
      {album.capa_url ? <img src={`${API}${album.capa_url}`} alt="" className="w-full h-full object-cover" /> : <Images className="w-6 h-6" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate">{album.titulo}</p>
      <p className="text-gray-500 text-xs">{album.data} • {album.total_publicadas}/{album.total_fotos} fotos publicadas{album.arquivado ? ' • ARQUIVADO' : ''}</p>
    </div>
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" disabled={first} onClick={() => onMove(-1)} className="text-gray-400" aria-label="Subir"><ChevronLeft className="w-4 h-4 rotate-90" /></Button>
      <Button variant="ghost" size="sm" disabled={last} onClick={() => onMove(1)} className="text-gray-400" aria-label="Descer"><ChevronRight className="w-4 h-4 rotate-90" /></Button>
    </div>
    <label className="flex items-center gap-2 text-xs text-gray-400"><Switch checked={!!album.publicado} onCheckedChange={(v) => onToggle({ publicado: v })} className="data-[state=unchecked]:bg-white/20" data-testid={`album-publicado-${album.album_id}`} />Publicado</label>
    <Button variant="ghost" size="sm" onClick={() => onToggle({ arquivado: !album.arquivado })} className="text-gray-400 hover:text-yellow-300" title={album.arquivado ? 'Restaurar' : 'Arquivar'} data-testid={`album-arquivar-${album.album_id}`}><Archive className="w-4 h-4" /></Button>
    <Link to={`/admin/galeria/${album.album_id}`} data-testid={`album-open-${album.album_id}`}><Button size="sm" className="bg-kairos-orange hover:bg-kairos-orange-dark text-white">Gerenciar fotos</Button></Link>
  </div>
);

const AlbunsView = () => {
  const [edicoes, setEdicoes] = useState([]);
  const [albuns, setAlbuns] = useState([]);
  const [novo, setNovo] = useState({ titulo: '', data: '' });

  const load = useCallback(async () => {
    try {
      const [e, a] = await Promise.all([galeriaApi.edicoes(), galeriaApi.albuns()]);
      setEdicoes(e.data); setAlbuns(a.data);
    } catch (err) { logger.error(err); toast.error('Erro ao carregar galeria'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id, patch) => { try { await galeriaApi.editarAlbum(id, patch); load(); } catch { toast.error('Erro ao atualizar álbum'); } };
  const move = async (edicaoId, idx, dir) => {
    const lista = albuns.filter((a) => a.edicao_id === edicaoId);
    const j = idx + dir; if (j < 0 || j >= lista.length) return;
    [lista[idx], lista[j]] = [lista[j], lista[idx]];
    await galeriaApi.reordenarAlbuns(lista.map((a) => a.album_id)); load();
  };
  const criar = async (edicaoId) => {
    if (!novo.titulo) return toast.error('Informe o título do álbum');
    try { await galeriaApi.criarAlbum({ edicao_id: edicaoId, titulo: novo.titulo, data: novo.data || null }); setNovo({ titulo: '', data: '' }); toast.success('Álbum criado'); load(); } catch { toast.error('Erro ao criar álbum'); }
  };

  return (
    <div className="space-y-8">
      {edicoes.map((e) => {
        const lista = albuns.filter((a) => a.edicao_id === e.edicao_id);
        return (
          <Card key={e.edicao_id} className="bg-obsidian-100 border-white/10" data-testid={`admin-edicao-${e.ano}`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="text-white text-xl font-semibold">{e.nome}</h2>
                  <p className="text-gray-500 text-sm">{e.data_inicio} a {e.data_fim} • {e.total_albuns} álbuns • {e.total_fotos} fotos</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-400"><Switch checked={!!e.publicado} onCheckedChange={async (v) => { await galeriaApi.editarEdicao(e.edicao_id, { publicado: v }); load(); }} className="data-[state=unchecked]:bg-white/20" data-testid={`edicao-publicada-${e.ano}`} />Edição publicada</label>
                  <a href="/memorias" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="border-white/10 text-gray-300"><ExternalLink className="w-4 h-4 mr-1" />Ver site</Button></a>
                  {e.total_fotos === 0 && edicoes.length > 1 && <Button variant="ghost" size="sm" onClick={async () => { if (!window.confirm('Excluir esta edição e seus álbuns vazios?')) return; try { await galeriaApi.excluirEdicao(e.edicao_id); toast.success('Edição excluída'); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); } }} className="text-red-400 hover:bg-red-500/10" data-testid={`excluir-edicao-${e.ano}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="space-y-2">
                {lista.filter((a) => !a.arquivado).map((a, i, arr) => <AlbumRow key={a.album_id} album={a} first={i === 0} last={i === arr.length - 1} onToggle={(p) => toggle(a.album_id, p)} onMove={(d) => move(e.edicao_id, i, d)} />)}
              </div>
              {lista.some((a) => a.arquivado) && (
                <details className="text-sm text-gray-400" data-testid={`arquivados-${e.ano}`}>
                  <summary className="cursor-pointer">Álbuns arquivados ({lista.filter((a) => a.arquivado).length})</summary>
                  <div className="space-y-2 mt-2">{lista.filter((a) => a.arquivado).map((a) => <AlbumRow key={a.album_id} album={a} first last onToggle={(p) => toggle(a.album_id, p)} onMove={() => {}} />)}</div>
                </details>
              )}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Input value={novo.titulo} onChange={(ev) => setNovo({ ...novo, titulo: ev.target.value })} placeholder="Novo álbum (ex.: Jantar Romântico)" className="bg-obsidian-50 border-white/10 text-white" data-testid="novo-album-titulo" />
                <Input type="date" value={novo.data} onChange={(ev) => setNovo({ ...novo, data: ev.target.value })} className="bg-obsidian-50 border-white/10 text-white sm:w-48" data-testid="novo-album-data" />
                <Button onClick={() => criar(e.edicao_id)} variant="outline" className="border-kairos-orange/40 text-kairos-orange" data-testid="novo-album-btn"><Plus className="w-4 h-4 mr-1" />Criar álbum</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const FotosView = ({ albumId }) => {
  const [album, setAlbum] = useState(null);
  const [fotos, setFotos] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, f] = await Promise.all([galeriaApi.albuns(), galeriaApi.fotos(albumId)]);
      setAlbum(a.data.find((x) => x.album_id === albumId) || null); setFotos(f.data);
    } catch (err) { logger.error(err); toast.error('Erro ao carregar fotos'); }
  }, [albumId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, data) => { try { const r = await galeriaApi.editarFoto(id, data); setFotos((p) => p.map((f) => (f.foto_id === id ? r.data : f))); } catch { toast.error('Erro ao atualizar foto'); } };
  const remover = async (id) => { if (!window.confirm('Remover esta foto da galeria?')) return; try { await galeriaApi.excluirFoto(id); setFotos((p) => p.filter((f) => f.foto_id !== id)); toast.success('Foto removida'); } catch { toast.error('Erro ao remover'); } };
  const capa = async (id) => { try { await galeriaApi.editarAlbum(albumId, { capa_foto_id: id }); toast.success('Capa definida'); load(); } catch { toast.error('Erro ao definir capa'); } };
  const mover = async (i, dir) => { const j = i + dir; if (j < 0 || j >= fotos.length) return; const arr = [...fotos]; [arr[i], arr[j]] = [arr[j], arr[i]]; setFotos(arr); await galeriaApi.reordenarFotos(albumId, arr.map((f) => f.foto_id)); };

  return (
    <div className="space-y-6">
      <Link to="/admin/galeria" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm" data-testid="voltar-albuns"><ArrowLeft className="w-4 h-4" />Voltar aos álbuns</Link>
      {album && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 className="text-white text-xl font-semibold" data-testid="admin-album-titulo">{album.titulo}</h2>
            <p className="text-gray-500 text-sm">{album.data} • {fotos.length} fotos ({fotos.filter((f) => f.publicado).length} publicadas)</p>
          </div>
          <div className="flex gap-2">
            <Input defaultValue={album.titulo} onBlur={(e) => e.target.value !== album.titulo && galeriaApi.editarAlbum(albumId, { titulo: e.target.value }).then(() => { toast.success('Título atualizado'); load(); })} className="bg-obsidian-50 border-white/10 text-white md:w-64" data-testid="album-titulo-input" />
            <Input defaultValue={album.descricao} placeholder="Descrição (opcional)" onBlur={(e) => e.target.value !== album.descricao && galeriaApi.editarAlbum(albumId, { descricao: e.target.value }).then(() => { toast.success('Descrição atualizada'); load(); })} className="bg-obsidian-50 border-white/10 text-white md:w-80" data-testid="album-descricao-input" />
          </div>
        </div>
      )}
      <PhotoUploader albumId={albumId} onUploaded={(novas) => setFotos((p) => [...p, ...novas])} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-fotos-grid">
        {fotos.map((f, i) => (
          <div key={f.foto_id} className={`rounded-xl overflow-hidden border ${f.publicado ? 'border-white/10' : 'border-red-500/40'} bg-obsidian-200`} data-testid={`admin-foto-${f.foto_id}`}>
            <div className="relative aspect-[4/3] bg-black">
              <img src={`${API}${f.thumb_url}`} alt={f.alt} loading="lazy" className={`w-full h-full object-cover ${f.publicado ? '' : 'opacity-40'}`} />
              {album?.capa_foto_id === f.foto_id && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-kairos-orange text-white text-[10px] font-semibold">CAPA</span>}
              {!f.publicado && <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-semibold">OCULTA</span>}
            </div>
            <div className="p-2 space-y-2">
              <Input defaultValue={f.alt} onBlur={(e) => e.target.value !== f.alt && patch(f.foto_id, { alt: e.target.value })} className="h-8 text-xs bg-obsidian-50 border-white/10 text-gray-300" title="Texto alternativo (acessibilidade)" data-testid={`foto-alt-${f.foto_id}`} />
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" disabled={i === 0} onClick={() => mover(i, -1)} aria-label="Mover para trás"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" disabled={i === fotos.length - 1} onClick={() => mover(i, 1)} aria-label="Mover para frente"><ChevronRight className="w-4 h-4" /></Button>
                </div>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-kairos-orange" onClick={() => capa(f.foto_id)} title="Definir como capa" data-testid={`foto-capa-${f.foto_id}`}><Star className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-white" onClick={() => patch(f.foto_id, { publicado: !f.publicado })} title={f.publicado ? 'Ocultar' : 'Publicar'} data-testid={`foto-toggle-${f.foto_id}`}>{f.publicado ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-400" onClick={() => remover(f.foto_id)} title="Remover" data-testid={`foto-remover-${f.foto_id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GaleriaPage = () => {
  const { albumId } = useParams();
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-galeria-page">
        <div>
          <h1 className="font-display text-3xl text-white flex items-center gap-3"><Images className="w-7 h-7 text-kairos-orange" />Galeria de Eventos</h1>
          <p className="text-gray-400 mt-1">Edições, álbuns por dia e fotos. Publique apenas fotos com autorização de uso de imagem; qualquer foto pode ser ocultada ou removida mediante solicitação.</p>
        </div>
        {albumId ? <FotosView albumId={albumId} /> : <AlbunsView />}
      </div>
    </AdminLayout>
  );
};

export default GaleriaPage;
